import { TablePaginationProps } from '@mui/material';
import { useLatest } from '@qfa/react-components';
import { useCallback, useMemo, useState } from 'react';
import { VariableSizeList } from 'react-window';

export interface usePaginationOptions {
  page?: number;
  count?: number;
  rows?: number;
  onPageChange?: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  scrollRef?: React.MutableRefObject<HTMLElement | VariableSizeList | null>;
  onNext?: (nextPageNumber: number) => void;
  onPrevious?: (previousPageNumber: number) => void;
}

const getPagedData = <TData>(data: TData[], offset: number, limit: number) => {
  if (offset > data.length || offset < 0) {
    console.error(`offset ${offset} out of range: ${data.length}`);
    return [];
  }
  return data.slice(offset, offset + limit);
};

const scrollToTop = (scrollRef: usePaginationOptions['scrollRef']) => {
  const element = scrollRef?.current;
  if (!element) {
    return;
  }
  if (element instanceof HTMLElement) {
    element.scroll({ top: 0, behavior: 'smooth' });
  }
  if (element instanceof VariableSizeList) {
    element.scrollTo(0);
  }
};

export const usePagination = (options: usePaginationOptions = {}) => {
  const {
    page: initialPage = 0,
    count: initialCount = -1,
    rows: initialRows = 25,
    onPageChange: pageCallback,
    onRowsPerPageChange: rowsCallback,
    scrollRef,
    onNext: nextCallback,
    onPrevious: previousCallback,
  } = options;
  const [page, setPage] = useState(initialPage);
  const currentPage = useLatest(page);
  const onNext = useLatest(nextCallback);
  const onPrevious = useLatest(previousCallback);
  const [count, setCount] = useState(initialCount);
  const [rows, setRows] = useState(initialRows);

  const onPageChange = useCallback(
    (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setPage(newPage);
      pageCallback?.(event, newPage);
      if (newPage > currentPage.current) {
        onNext.current?.(newPage);
      } else if (newPage < currentPage.current) {
        onPrevious.current?.(newPage);
      }
      scrollToTop(scrollRef);
    },
    [currentPage, onNext, onPrevious, pageCallback, scrollRef]
  );

  const onRowsPerPageChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setRows(Number(e.target.value));
      rowsCallback?.(e);
      setPage(0);
      scrollToTop(scrollRef);
    },
    [rowsCallback, scrollRef]
  );

  const props: TablePaginationProps = useMemo(() => {
    return {
      component: 'div' as const,
      page,
      count,
      onPageChange,
      onRowsPerPageChange,
      rowsPerPage: rows,
    };
  }, [count, onPageChange, onRowsPerPageChange, page, rows]);

  return {
    props,
    after: page * rows,
    limit: rows,
    setPage,
    setCount,
    setRows,
    getPagedData,
  };
};
